import { useRef, useState } from "react";
import api, { gbp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Download, FileSpreadsheet, Loader2, FileUp } from "lucide-react";
import { toast } from "sonner";
import { parseDate, fmtDate } from "@/components/DatePicker";

const ACTION_TONE = { update: "secondary", skip: "outline" };
const showDate = (iso) => { const d = parseDate(iso); return d ? fmtDate(d) : "—"; };

export default function ProposalImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, summary }
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const reset = () => { setPreview(null); setFileName(""); setBusy(false); };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/proposals/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "CASS-proposal-import-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx") && !f.name.toLowerCase().endsWith(".xls")) return toast.error("Please choose an Excel .xlsx file");
    setBusy(true); setFileName(f.name);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/proposals/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read the file"); reset(); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    const valid = preview.rows.filter((r) => r.action === "update");
    if (!valid.length) return toast.error("Nothing to import — no rows matched a client");
    setBusy(true);
    try {
      const { data } = await api.post("/proposals/import/commit", { rows: valid });
      toast.success(`Import complete — ${data.updated} client${data.updated === 1 ? "" : "s"} updated`);
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) failed to save`);
      setOpen(false); reset(); onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="import-proposals-btn">
        <FileUp className="w-4 h-4 mr-2" /> Import Proposals
      </Button>
      <DialogContent className="max-w-4xl" data-testid="proposal-import-dialog">
        <DialogHeader><DialogTitle>Bring in existing proposals</DialogTitle></DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <p className="text-sm text-cass-slate">Import the summary details of proposals created outside this tool onto matching client records — so migrated clients carry their current agreement and flag correctly on Renewals. Rows are matched to existing clients by <b>company number</b>, then <b>legal or trading name</b>. Unmatched rows are skipped and listed. Leave <b>Proposal expiry date</b> blank to auto-calculate it (start + 1 year − 1 day); the renewal date is always set to 30 days before expiry.</p>
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-proposal-template-btn"><Download className="w-4 h-4 mr-2" />Download Excel template</Button>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <FileSpreadsheet className="w-8 h-8 mx-auto text-cass-slate mb-2" />
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="proposal-import-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-cass-navy text-white" data-testid="choose-proposal-file-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Choose spreadsheet
              </Button>
              {fileName && <div className="text-xs text-cass-slate mt-2">{fileName}</div>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm items-center" data-testid="proposal-import-summary">
              <Badge variant="secondary">{preview.summary.update} to update</Badge>
              {preview.summary.skip > 0 && <Badge variant="outline">{preview.summary.skip} skipped</Badge>}
              <span className="text-cass-slate">of {preview.summary.total} rows</span>
            </div>
            <div className="max-h-[45vh] overflow-y-auto border border-border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">Row</TableHead>
                    <TableHead>Matched client</TableHead>
                    <TableHead>Proposal no.</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Renewal</TableHead>
                    <TableHead className="text-right">MRR</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.row} data-testid={`proposal-import-row-${r.row}`} className={r.action === "skip" ? "opacity-70" : ""}>
                      <TableCell className="text-cass-slate">{r.row}</TableCell>
                      <TableCell className="font-medium text-cass-navy">{r.matched_name || <span className="text-cass-slate font-normal">{r.name}</span>}</TableCell>
                      <TableCell className="text-sm">{r.ref || "—"}</TableCell>
                      <TableCell className="text-sm">{showDate(r.start)}</TableCell>
                      <TableCell className="text-sm">{showDate(r.expiry)}</TableCell>
                      <TableCell className="text-sm">{showDate(r.renewal)}</TableCell>
                      <TableCell className="text-right tabular text-sm">{r.monthly != null ? gbp(r.monthly) : "—"}</TableCell>
                      <TableCell><Badge variant={ACTION_TONE[r.action]}>{r.action}</Badge></TableCell>
                      <TableCell className="text-xs text-amber-700">{r.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-cass-slate">Imported values overwrite existing current-proposal details on each matched client. No new client records are created.</p>
          </div>
        )}

        <DialogFooter>
          {preview && <Button variant="ghost" onClick={reset} data-testid="proposal-import-back-btn">Choose a different file</Button>}
          {preview && (
            <Button onClick={confirm} disabled={busy || !preview.summary.update} className="bg-cass-teal text-white" data-testid="confirm-proposal-import-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update {preview.summary.update} client{preview.summary.update === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
